import Axios from 'axios';

import {
  clearAuthTokens,
  hasAuthSession,
  isRememberMeEnabled,
  persistAuthSession,
} from '../utils/authTokenStorage';
import { EnvConfig } from './EnvConfig';

const axios = Axios.create({
  withCredentials: true,
});

let isRefreshing = false;
let pendingRequests: Array<() => void> = [];

const executePendingRequests = () => {
  pendingRequests.forEach((resolve) => resolve());
  pendingRequests = [];
};

const addPendingRequest = (): Promise<void> => {
  return new Promise((resolve) => {
    pendingRequests.push(resolve);
  });
};

const EXCLUDED_ROUTES = [
  '/v1/auth/refresh',
  '/v1/auth/login',
  '/v1/auth/logout',
];

const shouldSkipRefresh = (url: string): boolean => {
  return EXCLUDED_ROUTES.some((route) => url.includes(route));
};

const handleRefreshError = async () => {
  try {
    await axios.post('/v1/auth/logout');
  } catch {
    // ignore logout errors while clearing local session
  }
  clearAuthTokens();
  window.location.href = '/login';
};

axios.interceptors.request.use(
  async function (config) {
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    config.baseURL = EnvConfig.apiUrl;
    config.withCredentials = true;

    if (config.params) {
      const query: Record<string, unknown> = {};
      for (const key in config.params) {
        if (config.params[key]) {
          query[key] = config.params[key];
        }
      }
      config.params = query;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axios.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !shouldSkipRefresh(originalRequest.url || '') &&
      hasAuthSession() &&
      window.location.pathname !== '/login'
    ) {
      if (isRefreshing) {
        await addPendingRequest();
        return axios(originalRequest);
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.patch('/v1/auth/refresh', {
          rememberMe: isRememberMeEnabled(),
        });
        persistAuthSession({ rememberMe: isRememberMeEnabled() });

        executePendingRequests();
        isRefreshing = false;

        return axios(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        await handleRefreshError();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export const api = axios;
